import { useTheme } from "@emotion/react";
import { Skeleton, Tooltip } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { Button, Chip, Typography } from "@avenue-8/sidekick-ui";
import { CopyableText } from "@features/Users/components/CopyableText";
import { StatusPill } from "@shared/components/StatusPill";
import { FormattedDate } from "@shared/components/FormattedDate/FormattedDate";
import { TablePagination } from "@shared/components/TablePagination";
import type { ListSubscriptionsQueryResponse } from "@shared/generated/radar/models/subscriptionAuditController/ListSubscriptions";
import { getToneColors } from "./event-subscriptions-audit-helpers";
import {
  Card,
  CardFooter,
  CardFooterActions,
  CardHeader,
  CardMetaRow,
  CardsList,
  ChipsRow,
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconWrap,
  HeaderNameTag,
  TargetUrlText,
} from "./EventSubscriptionsAudit.styles";

type Subscription = ListSubscriptionsQueryResponse["items"][number];

const activitySummary = (subscription: Subscription) => {
  if (subscription.autoDisabledAt) {
    return {
      label: "Auto-disabled after repeated failures",
      tone: "error" as const,
      detailLabel: "Auto-disabled",
      detailDate: subscription.autoDisabledAt,
    };
  }
  if (subscription.consecutiveFailureCount > 0) {
    return {
      label: `${subscription.consecutiveFailureCount} recent failure${subscription.consecutiveFailureCount === 1 ? "" : "s"}`,
      tone: "warning" as const,
      detailLabel: "Last failure",
      detailDate: subscription.lastFailureAt,
    };
  }
  if (subscription.lastSuccessAt) {
    return {
      label: "Delivering successfully",
      tone: "success" as const,
      detailLabel: "Last delivered",
      detailDate: subscription.lastSuccessAt,
    };
  }
  return {
    label: "No deliveries yet",
    tone: "neutral" as const,
    detailLabel: null,
    detailDate: null,
  };
};

type Props = {
  subscriptions: Subscription[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onViewDeliveries: (subscription: Subscription) => void;
};

export const SubscriptionsAuditList = (props: Props) => {
  const {
    subscriptions,
    isLoading,
    page,
    pageSize,
    total,
    onPageChange,
    onViewDeliveries,
  } = props;
  const theme = useTheme();
  const totalPages = Math.ceil(total / pageSize);

  if (isLoading) {
    return (
      <CardsList>
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <Skeleton key={index} variant="rounded" height={140} />
          ))}
      </CardsList>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIconWrap>
          <WebhookOutlinedIcon />
        </EmptyStateIconWrap>
        <Typography variant="h6">No subscriptions found</Typography>
        <EmptyStateDescription variant="body2">
          Try adjusting your filters, or check back once a user has created an
          event subscription.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <>
      <CardsList>
        {subscriptions.map((subscription) => {
          const activity = activitySummary(subscription);
          return (
            <Card key={subscription.id}>
              <CardHeader>
                <Typography variant="subtitle1">
                  {subscription.name}
                </Typography>
                <StatusPill
                  label={subscription.enabled ? "Enabled" : "Disabled"}
                  {...getToneColors(
                    subscription.enabled ? "success" : "neutral",
                    theme,
                  )}
                />
              </CardHeader>

              <CardMetaRow>
                <Typography variant="body2">
                  ID
                </Typography>
                <CopyableText text={subscription.id}>
                  {subscription.id}
                </CopyableText>
              </CardMetaRow>

              <CardMetaRow>
                <Typography variant="body2">
                  Owner
                </Typography>
                {subscription.userName && (
                  <Typography>{subscription.userName}</Typography>
                )}
                <CopyableText text={subscription.userId}>
                  {subscription.userId}
                </CopyableText>
              </CardMetaRow>

              <Tooltip title={subscription.targetUrl}>
                <TargetUrlText>{subscription.targetUrl}</TargetUrlText>
              </Tooltip>

              <ChipsRow>
                {subscription.eventTypes.map((eventType) => (
                  <Chip key={eventType} size="small" label={eventType} />
                ))}
              </ChipsRow>

              <CardMetaRow>
                <Typography variant="body2">
                  Headers
                </Typography>
                {subscription.headerNames.length > 0 ? (
                  <ChipsRow>
                    {subscription.headerNames.map((name) => (
                      <HeaderNameTag key={name}>{name}</HeaderNameTag>
                    ))}
                  </ChipsRow>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No headers
                  </Typography>
                )}
              </CardMetaRow>

              <CardFooter>
                <CardFooterActions>
                  <StatusPill
                    label={activity.label}
                    {...getToneColors(activity.tone, theme)}
                  />
                  <Button
                    variant="text"
                    size="small"
                    aria-label={`View deliveries for ${subscription.name}`}
                    startIcon={<HistoryOutlinedIcon fontSize="small" />}
                    onClick={() => onViewDeliveries(subscription)}
                  >
                    View deliveries
                  </Button>
                </CardFooterActions>

                {activity.detailDate && (
                  <Typography variant="caption" color="textSecondary">
                    {activity.detailLabel}{" "}
                    <FormattedDate
                      value={activity.detailDate}
                      format="MMM d, yyyy h:mm:ss a"
                    />
                  </Typography>
                )}

                {subscription.lastErrorMessage && (
                  <Typography variant="body2" color="error">
                    {subscription.lastErrorMessage}
                  </Typography>
                )}

                <Typography variant="caption" color="textSecondary">
                  Created{" "}
                  <FormattedDate
                    value={subscription.createdAt}
                    format="MMM d, yyyy h:mm:ss a"
                  />
                </Typography>
              </CardFooter>
            </Card>
          );
        })}
      </CardsList>
      <TablePagination
        page={page}
        totalPages={totalPages}
        onChange={onPageChange}
      />
    </>
  );
};
